import mealImageUrl from "@/assets/meal2.svg"
import snackBowlImageUrl from "@/assets/snack-bowl.svg"
import { Button } from "@/components/chadcn/Button"
import { textForFormContext, type BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import type { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation"
import { ROUTES, buildPath } from "@/routes"
import { DateTimeFormatter } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { LockClosedIcon } from "@radix-ui/react-icons"
import { useNavigate } from "react-router"
import {
  foodLogsByDate,
  readingValueBackgroundColorFor,
  readingValueBorderColorFor,
} from "../dashboardData"

type FoodLogListProps = {
  observations: BloodGlucoseObservation[]
}

const DAY_HEADER_FORMAT = DateTimeFormatter.ofPattern("EEEE, MMM dd").withLocale(Locale.US)
const TIME_FORMAT = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US)

const snackContexts = new Set<BloodGlucoseFormContext>([
  "snack-before-breakfast",
  "snack-between-breakfast-and-lunch",
  "snack-between-lunch-and-dinner",
  "snack-after-dinner",
])

const FoodLogList = ({ observations }: FoodLogListProps) => {
  const navigate = useNavigate()
  const byDate = foodLogsByDate(observations)
  const entries = Object.entries(byDate).sort(([a], [b]) => (a < b ? 1 : -1))

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex justify-end">
        <Button type="button" size="sm" onClick={() => navigate(ROUTES.ADD_FOOD_LOG)}>
          + Add food to log
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className="py-4 text-center text-sm text-primary-contrast/60">
          No food logs found for selected week
        </div>
      ) : (
        entries.map(([dateKey, dayObservations]) => {
          const heading = dayObservations[0].dateTime.toLocalDate().format(DAY_HEADER_FORMAT)
          return (
            <div key={dateKey} className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-primary-contrast">{heading}</h3>
              <div className="flex flex-col gap-2">
                {dayObservations.map((observation) => {
                  const form = observation.form!
                  const icon = snackContexts.has(form.context) ? snackBowlImageUrl : mealImageUrl
                  const time = observation.dateTime.format(TIME_FORMAT)
                  const reading = observation.reading
                  const target = reading
                    ? buildPath(ROUTES.MEASUREMENT.EDIT, {
                        measurementId: reading.id,
                      })
                    : buildPath(ROUTES.FOOD_LOG_EDIT, { formId: form.id })
                  return (
                    <button
                      key={form.id}
                      type="button"
                      onClick={() =>
                        navigate(target, {
                          state: { initialTab: "meal-logs" },
                        })
                      }
                      className="rounded-lg border p-3 text-left"
                      style={{
                        borderColor: readingValueBorderColorFor(reading?.classification),
                        backgroundColor: readingValueBackgroundColorFor(reading?.classification),
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-primary-contrast">
                          <img alt="" src={icon} className="h-5 w-5" />
                          {textForFormContext(form.context)}
                          {!observation.isFormEditable && (
                            <LockClosedIcon aria-label="Locked food log" className="h-4 w-4" />
                          )}
                        </span>
                        {reading && (
                          <span className="text-sm font-semibold text-primary-contrast">
                            {reading.bloodGlucoseInMilligramsPerDeciliter} mg/dL
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-primary-contrast/70">
                        {form.mealContents || "N/A"}
                      </p>
                      <div className="mt-3 flex items-center justify-between border-t border-primary-contrast/10 pt-2 text-xs text-primary-contrast/60">
                        <span className="text-xs text-primary-contrast/60">
                          Carbs{" "}
                          {form.carbsInGrams && form.carbsInGrams !== null
                            ? `${form.carbsInGrams}g`
                            : "N/A"}
                        </span>
                        <span className="text-xs text-primary-contrast/60">
                          Protein{" "}
                          {form.proteinInGrams && form.proteinInGrams !== null
                            ? `${form.proteinInGrams}g`
                            : "N/A"}
                        </span>
                        <span className="text-xs text-primary-contrast/60">{time}</span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default FoodLogList
